/* vaultCrypto.js — WebCrypto AES-GCM helpers for the Verified Evidence Vault.
 *
 * Design:
 *   1. Passphrase → 256-bit AES-GCM key via PBKDF2-SHA256 (200,000 iters)
 *      with a per-user salt (stored in localStorage under `vault:salt`).
 *   2. Encryption: 12-byte random IV per file; ciphertext = IV || AES-GCM(payload).
 *   3. Recovery is a Salt File — a plain .txt containing the hex-encoded
 *      per-user salt. On a new device the user imports this salt, enters
 *      their passphrase, and can decrypt files encrypted on the old
 *      device. The salt is the ONLY recovery artifact; there is no
 *      separate mnemonic phrase.
 */

const enc = new TextEncoder();
const SALT_STORAGE_KEY = "vault:salt";
const PBKDF2_ITERATIONS = 200_000;

function toHex(buf) {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0")).join("");
}

function fromHex(hex) {
  const arr = new Uint8Array(hex.length / 2);
  for (let i = 0; i < arr.length; i += 1) arr[i] = parseInt(hex.substr(i * 2, 2), 16);
  return arr;
}

export function getOrCreateSalt() {
  const existing = localStorage.getItem(SALT_STORAGE_KEY);
  if (existing) return fromHex(existing);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  localStorage.setItem(SALT_STORAGE_KEY, toHex(salt));
  return salt;
}

export function setSaltHex(hex) {
  localStorage.setItem(SALT_STORAGE_KEY, hex);
}

export function getSaltHex() {
  return localStorage.getItem(SALT_STORAGE_KEY) || "";
}

async function deriveKey(passphrase, saltBytes) {
  const baseKey = await crypto.subtle.importKey(
    "raw", enc.encode(passphrase), { name: "PBKDF2" }, false, ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: saltBytes, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function encryptFile(file, passphrase) {
  const salt = getOrCreateSalt();
  const key = await deriveKey(passphrase, salt);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = await file.arrayBuffer();
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
  // Envelope: 12-byte IV || ciphertext
  const envelope = new Uint8Array(iv.byteLength + ciphertext.byteLength);
  envelope.set(iv, 0);
  envelope.set(new Uint8Array(ciphertext), iv.byteLength);
  return new Blob([envelope], { type: "application/octet-stream" });
}

export async function decryptFile(blob, passphrase) {
  const salt = getOrCreateSalt();
  const key = await deriveKey(passphrase, salt);
  const buf = new Uint8Array(await blob.arrayBuffer());
  const iv = buf.slice(0, 12);
  const ciphertext = buf.slice(12);
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
  return new Blob([plaintext]);
}

/**
 * Download the user's Vault Salt File — the primary recovery artifact.
 * This file, together with the user's passphrase, is the ONLY way to
 * decrypt files encrypted by this vault on another device. Store it
 * offline (paper, hardware key, encrypted drive).
 */
export function downloadSaltRecoveryFile(userEmail, saltHex) {
  const stamp = new Date().toISOString();
  const body = [
    "BEATCREDSI — SOVEREIGN VAULT SALT FILE",
    "======================================",
    `Account:   ${userEmail}`,
    `Generated: ${stamp}`,
    "",
    "PRIMARY RECOVERY ARTIFACT — SALT (hex, 32 chars):",
    saltHex,
    "",
    "Instructions:",
    "  • This salt + your passphrase = the ONLY way to decrypt your vault",
    "    files on another device. There is no mnemonic phrase and no",
    "    server-side backup.",
    "  • On a new device: open the Vault, choose \"Import a recovery salt\",",
    "    paste the hex string above, then enter your passphrase.",
    "  • Store this file offline (paper, hardware key, encrypted drive).",
    "  • Losing this file AND your passphrase means your encrypted files",
    "    cannot be recovered by anyone — not even BeatCredsi staff.",
    "",
    "This document is class-A confidential. Do not email it or store in a shared drive.",
  ].join("\n");
  const blob = new Blob([body], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `beatcredsi-vault-salt-${stamp.slice(0, 10)}.txt`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
